import { z } from 'zod';
import {
  FieldKeySchema,
  IdSchema,
  MetadataSchema,
  PluginExtensionIdSchema,
  SortSchema,
} from './common.js';
import { FormatSpecSchema } from './format.js';

/** Visualizaciones incluidas en la plataforma. */
export const BUILTIN_VISUALIZATION_TYPES = [
  'table',
  'grouped-table',
  'pivot',
  'kpi',
  'bar',
  'column',
  'line',
  'area',
  'pie',
  'donut',
  'scatter',
  'treemap',
  'waterfall',
  'map',
  'heatmap',
] as const;
export type BuiltinVisualizationType = (typeof BUILTIN_VISUALIZATION_TYPES)[number];

/** Las visualizaciones aportadas por plugins usan el prefijo `plugin:`. */
export const PluginVisualizationTypeSchema = PluginExtensionIdSchema;

export const VisualizationTypeSchema = z.union([
  z.enum(BUILTIN_VISUALIZATION_TYPES),
  PluginVisualizationTypeSchema,
]);
export type VisualizationType = z.infer<typeof VisualizationTypeSchema>;

/** Canales visuales a los que se asignan los campos de la consulta (por su `key`). */
export const ENCODING_CHANNELS = [
  'x',
  'y',
  'color',
  'size',
  'label',
  'tooltip',
  'detail',
  'rows',
  'columns',
  'values',
  'geo',
] as const;
export const EncodingChannelSchema = z.enum(ENCODING_CHANNELS);
export type EncodingChannel = z.infer<typeof EncodingChannelSchema>;

const HexColorSchema = z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Color hexadecimal #RRGGBB');

/** Estilo visual de la tarjeta: tipografia y colores de presentacion. */
export const VisualizationStyleSchema = z
  .strictObject({
    fontSize: z.enum(['sm', 'md', 'lg', 'xl']).default('md'),
    /** Color de texto principal; ausente = tema de la app. */
    foreground: HexColorSchema.optional(),
    /** Fondo de la visualizacion; ausente = superficie de la tarjeta. */
    background: HexColorSchema.optional(),
    /** Paleta de series (hasta 8). Vacia = tokens `--chart-N` del tema. */
    palette: z.array(HexColorSchema).max(8).default([]),
  })
  .prefault({});
export type VisualizationStyle = z.infer<typeof VisualizationStyleSchema>;
export type VisualizationFontSize = VisualizationStyle['fontSize'];

export const AxisSpecSchema = z.strictObject({
  show: z.boolean().default(true),
  title: z.string().max(200).optional(),
  scale: z.enum(['auto', 'linear', 'log', 'time', 'category']).default('auto'),
  min: z.number().optional(),
  max: z.number().optional(),
  format: FormatSpecSchema.optional(),
});
export type AxisSpec = z.infer<typeof AxisSpecSchema>;

/** Operadores de formato condicional (numeros, texto y booleanos). */
export const CONDITIONAL_OPERATORS = [
  'eq',
  'neq',
  'gt',
  'gte',
  'lt',
  'lte',
  'between',
  'in',
  'contains',
  'isEmpty',
] as const;
export const ConditionalOperatorSchema = z.enum(CONDITIONAL_OPERATORS);
export type ConditionalOperator = z.infer<typeof ConditionalOperatorSchema>;

const ConditionalOperandSchema = z.union([z.string(), z.number(), z.boolean()]);

export const ConditionalFormatRuleSchema = z
  .strictObject({
    id: IdSchema,
    key: FieldKeySchema,
    operator: ConditionalOperatorSchema,
    /** Operandos: numeros para rangos, texto/booleanos para igualdad, etc. */
    values: z.array(ConditionalOperandSchema).max(8).default([]),
    color: HexColorSchema.optional(),
    background: HexColorSchema.optional(),
  })
  .superRefine((rule, ctx) => {
    const count = rule.values.length;
    const need = (min: number, max = min): void => {
      if (count < min || count > max) {
        ctx.addIssue({
          code: 'custom',
          message: `El operador ${rule.operator} requiere entre ${String(min)} y ${String(max)} valores`,
          path: ['values'],
        });
      }
    };
    switch (rule.operator) {
      case 'isEmpty':
        need(0);
        break;
      case 'between':
        need(2);
        break;
      case 'in':
        need(1, 8);
        break;
      default:
        need(1);
    }
  });
export type ConditionalFormatRule = z.infer<typeof ConditionalFormatRuleSchema>;
export type ConditionalFormatRuleInput = z.input<typeof ConditionalFormatRuleSchema>;

export const MapConfigSchema = z.strictObject({
  kind: z.enum(['choropleth', 'point', 'bubble', 'heatmap']),
  /** Campo con codigos o nombres geograficos (choropleth). */
  geoKey: FieldKeySchema.optional(),
  latitudeKey: FieldKeySchema.optional(),
  longitudeKey: FieldKeySchema.optional(),
  geoRole: z.enum(['country', 'region', 'state', 'city', 'postal-code']).optional(),
  /** Id de un mapa base registrado en la plataforma; nunca una URL con token. */
  basemapId: IdSchema.optional(),
  /** Id de un conjunto de geometrias (paises, estados...) registrado en la plataforma. */
  regionSetId: IdSchema.optional(),
  initialView: z
    .strictObject({
      center: z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]),
      zoom: z.number().min(0).max(22),
    })
    .optional(),
});
export type MapConfig = z.infer<typeof MapConfigSchema>;

/**
 * Especificacion declarativa de la visualizacion: COMO se muestra un Analysis State.
 * Referencia campos de la consulta por clave; no contiene datos ni componentes de UI.
 * Cambiar el `type` no altera la consulta analitica.
 */
export const VisualizationSpecSchema = z.strictObject({
  type: VisualizationTypeSchema,
  title: z.string().max(200).optional(),
  encodings: z.partialRecord(EncodingChannelSchema, z.array(FieldKeySchema)).default({}),
  axes: z
    .strictObject({
      x: AxisSpecSchema.optional(),
      y: AxisSpecSchema.optional(),
      y2: AxisSpecSchema.optional(),
    })
    .default({}),
  grouping: z
    .strictObject({
      stacking: z.enum(['none', 'stacked', 'percent']).default('none'),
      seriesKey: FieldKeySchema.optional(),
    })
    .prefault({}),
  /** Orden de presentacion; no modifica el orden de la consulta. */
  sorting: z.array(SortSchema).default([]),
  formatting: z
    .strictObject({
      fields: z.record(FieldKeySchema, FormatSpecSchema).default({}),
      paletteId: IdSchema.optional(),
      conditional: z.array(ConditionalFormatRuleSchema).default([]),
    })
    .prefault({}),
  legend: z
    .strictObject({
      show: z.boolean().default(true),
      position: z.enum(['top', 'bottom', 'left', 'right']).default('bottom'),
    })
    .prefault({}),
  labels: z
    .strictObject({
      show: z.boolean().default(false),
      position: z.enum(['auto', 'inside', 'outside']).default('auto'),
    })
    .prefault({}),
  tooltip: z
    .strictObject({
      show: z.boolean().default(true),
      /** Campos adicionales; vacio = los campos visibles. */
      fields: z.array(FieldKeySchema).default([]),
    })
    .prefault({}),
  map: MapConfigSchema.optional(),
  interaction: z
    .strictObject({
      selectable: z.boolean().default(true),
      crossFilter: z.boolean().default(true),
      drillOnClick: z.boolean().default(true),
      zoom: z.boolean().default(false),
    })
    .prefault({}),
  /** Tipografia y colores de la visualizacion (independientes del tema de la app). */
  style: VisualizationStyleSchema,
  /** Opciones propias de visualizaciones de plugins. */
  options: MetadataSchema.default({}),
});
export type VisualizationSpec = z.infer<typeof VisualizationSpecSchema>;
export type VisualizationSpecInput = z.input<typeof VisualizationSpecSchema>;
